import { FormEventHandler, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { apiErrorToast, validatePassword } from "Utils";
import * as yup from "Utils/yup";
import { useCompletePasswordResetMutation } from "../services/passwordReset.api";

type ChangePasswordFormData = {
  newPassword: string;
  confirmPassword: string;
};

type UseChangePasswordForm = {
  errors: FieldErrors<ChangePasswordFormData>;
  isPasswordChangeSuccessful: boolean;
  isPasswordChangeError: boolean;
  methods: UseFormReturn<ChangePasswordFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const schema: yup.SchemaOf<ChangePasswordFormData> = yup.object({
  resetToken: yup.string(),
  newPassword: validatePassword().required(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Passwords must match")
    .required(),
});

const useChangePasswordForm = (
  verificationToken: string | undefined,
): UseChangePasswordForm => {
  const [isPasswordChangeSuccessful, setIsPasswordChangeSuccessful] =
    useState(false);
  const defaultValues = useMemo(
    () => ({
      newPassword: "",
      confirmPassword: "",
    }),
    [],
  );

  const [changePassword, { isError: isPasswordChangeError }] =
    useCompletePasswordResetMutation();

  const methods = useForm<ChangePasswordFormData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<ChangePasswordFormData> = async (data) => {
    try {
      if (verificationToken) {
        await changePassword({
          body: {
            password: data.newPassword,
            verification_token: verificationToken,
          },
        }).unwrap();

        setIsPasswordChangeSuccessful(true);
      }
    } catch (error) {
      apiErrorToast(error, "Unable to set new password");
      setIsPasswordChangeSuccessful(false);
    }
  };

  return {
    errors,
    isPasswordChangeSuccessful,
    isPasswordChangeError,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useChangePasswordForm;
